import type { MentionNodeAttrs, MentionOptions } from "@tiptap/extension-mention";
import Mention from "@tiptap/extension-mention";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  type JSONContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  ReactRenderer,
} from "@tiptap/react";
import type { SuggestionKeyDownProps, SuggestionOptions } from "@tiptap/suggestion";
import { AlertTriangleIcon } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import type { VariableDefinition } from "@babackapp/variables-registry";


type MentionListProps = {
  items: VariableDefinition[];
  command: (item: VariableDefinition) => void;
  unavailableVariableIds?: Set<string>;
};

const MentionList = forwardRef<
  { onKeyDown: (p: SuggestionKeyDownProps) => boolean },
  MentionListProps
>(({ items, command, unavailableVariableIds }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const unavailableIds = unavailableVariableIds ?? new Set<string>();

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) command(item);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((index) => (index + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((index) => (index + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  return (
    <div className="bg-background border border-border rounded-md shadow-md p-1 min-w-[220px]">
      {items.length ? (
        items.map((item, index) => {
          const isUnavailable = unavailableIds.has(item.id);
          return (
            <button
              key={item.id}
              onClick={() => selectItem(index)}
              onMouseDown={(e) => e.preventDefault()}
              type="button"
              title={isUnavailable ? "This variable may not be available for the current trigger type" : undefined}
              className={`flex items-center gap-1.5 w-full text-left px-2 py-1.5 rounded text-xs ${
                isUnavailable ? "opacity-50" : ""
              } ${
                index === selectedIndex
                  ? "bg-muted text-foreground"
                  : "hover:bg-muted text-foreground"
              }`}
            >
              {isUnavailable && <AlertTriangleIcon className="size-3 shrink-0 text-destructive" />}
              <span>{item.name} ({item.id})</span>
            </button>
          );
        })
      ) : (
        <div className="px-2 py-2 text-xs text-muted-foreground">
          No results
        </div>
      )}
    </div>
  );
});

MentionList.displayName = "MentionList";


export function createVariablesMentionExtension(
  variables: VariableDefinition[],
  unavailableVariableIds: Set<string> = new Set(),
) {
  const suggestion: Omit<SuggestionOptions<VariableDefinition>, "editor"> = {
    char: "@",
    allowSpaces: false,
    startOfLine: false,
    // Allow triggering after any character (including letters/numbers)
    // Some Tiptap versions support `allowedPrefixes`; setting it to null allows any prefix.
    // Cast to any to avoid type friction across versions.
    ...({ allowedPrefixes: null } as any),
    allow: () => true,
    command: ({ editor, range, props }) => {
      const { state } = editor;
      const marks = state.storedMarks || state.selection.$from.marks();
      const markTypes = marks.map((mark) => ({
        type: mark.type.name,
        attrs: mark.attrs,
      }));

      const nodeAfter = state.selection.$to.nodeAfter;
      const overrideSpace = nodeAfter?.text?.startsWith(" ");
      if (overrideSpace) {
        range.to += 1;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: "mention",
            attrs: { ...props, mentionSuggestionChar: "@" },
            marks: markTypes,
          },
          {
            type: "text",
            text: " ",
            marks: markTypes,
          },
        ])
        .run();

      editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
    },
    items: ({ query }) => {
      const q = query?.toLowerCase() ?? "";
      const list = variables
        .filter((item) => item.pickable !== false)
        .filter((item) =>
          [item.name, item.id].some((v) => v.toLowerCase().includes(q)),
        )
        .slice(0, 10);
      return list;
    },
    render: () => {
      let component: ReactRenderer<
        { onKeyDown: (p: SuggestionKeyDownProps) => boolean },
        MentionListProps
      > | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props) => {
          try {
            document.body.dataset.mentionOpen = "true";
          } catch {}

          component = new ReactRenderer(MentionList, {
            props: {
              items: props.items as VariableDefinition[],
              command: (item: VariableDefinition) =>
                props.command({ id: item.id, label: item.name }),
              unavailableVariableIds,
            },
            editor: props.editor,
          });

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component!.element as Element,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
          });
        },
        onUpdate(props) {
          component?.updateProps({
            items: props.items as VariableDefinition[],
            command: (item: VariableDefinition) =>
              props.command({ id: item.id, label: item.name }),
            unavailableVariableIds,
          });
          popup?.[0]?.setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },
        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component && component.ref
            ? component.ref.onKeyDown(props)
            : false;
        },
        onExit() {
          try {
            delete document.body.dataset.mentionOpen;
          } catch {}
          popup?.[0]?.destroy();
          popup = null;
          component?.destroy();
          component = null;
        },
      };
    },
  };

  const MentionWithNodeView = Mention.extend({
    marks: "_",
    addStorage() {
      return { unavailableVariableIds };
    },
    addNodeView() {
      return ReactNodeViewRenderer(MentionNodeView);
    },
  });

  return MentionWithNodeView.configure({
    suggestion: suggestion as Omit<
      SuggestionOptions<VariableDefinition, MentionNodeAttrs>,
      "editor"
    >,
    renderText({
      node,
    }: {
      options: MentionOptions<VariableDefinition, MentionNodeAttrs>;
      node: ProseMirrorNode;
      suggestion: SuggestionOptions<VariableDefinition, MentionNodeAttrs> | null;
    }) {
      const attrs = node.attrs as MentionNodeAttrs;
      return `{{${attrs.id}}}`;
    },
    renderHTML({
      options,
      node,
    }: {
      options: MentionOptions<VariableDefinition, MentionNodeAttrs>;
      node: ProseMirrorNode;
      suggestion: SuggestionOptions<VariableDefinition, MentionNodeAttrs> | null;
    }) {
      const attrs = node.attrs as MentionNodeAttrs;
      const text = `${options.suggestion.char}${attrs.label ?? attrs.id}`;
      return [
        "span",
        { ...(options.HTMLAttributes || {}), "data-type": "mention" },
        text,
      ];
    },
    HTMLAttributes: {
      class: "mention",
      style:
        "background: rgba(0,0,0,0.05); padding: 0 2px; border-radius: 3px;",
    },
  });
}

// craftsman-ignore: TS002
export default createVariablesMentionExtension;

function MentionNodeView(props: NodeViewProps) {
  const attrs = props.node.attrs as MentionNodeAttrs;
  const label = attrs.label ?? attrs.id ?? "";
  const unavailableIds = (props.extension.storage as { unavailableVariableIds: Set<string> }).unavailableVariableIds;
  const isUnavailable = attrs.id ? unavailableIds.has(attrs.id) : false;
  return (
    <NodeViewWrapper
      as="span"
      className={`mention px-1 whitespace-nowrap text-xs rounded-sm ${
        isUnavailable
          ? "border-b border-b-destructive/50 bg-destructive/10 text-destructive"
          : "border-b border-b-primary/30 bg-primary/10 text-primary"
      }`}
      data-type="mention"
      data-id={attrs.id ?? ""}
      contentEditable={false}
      title={isUnavailable ? "This variable may not be available for the current trigger type" : undefined}
    >
      {isUnavailable && <AlertTriangleIcon className="inline size-3 mr-0.5 align-text-bottom" />}
      @{label}
    </NodeViewWrapper>
  );
}

/**
 * Parses a string containing {{variable}} syntax and returns a TipTap JSON document
 * where each {{variable}} is replaced by a mention node.
 *
 * Example:
 *   Input: "/track{{shop.name}}"
 *   Output: {
 *     type: "doc",
 *     content: [
 *       {
 *         type: "paragraph",
 *         content: [
 *           { type: "text", text: "/track" },
 *           { type: "mention", attrs: { id: "shop.name", label: "Shop Name" } }
 *         ]
 *       }
 *     ]
 *   }
 */
export function parseMentionsFromText(
  text: string,
  variables: VariableDefinition[],
): JSONContent {
  // Clean up trailing empty paragraphs and <br> tags that TipTap adds
  const cleanedText = text
    .replace(/<p><\/p>$/g, "") // Remove trailing empty <p></p>
    .replace(/<br\s*\/?>$/g, ""); // Remove trailing <br> or <br/>

  const regex = /\{\{([^}]+)\}\}/g;
  const nodes: JSONContent[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(cleanedText)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      nodes.push({
        type: "text",
        text: cleanedText.slice(lastIndex, match.index),
      });
    }
    // Add the mention node
    const variableId = match[1];
    const variable =
      variables.find((v) => v.id === variableId) ||
      variables.find((v) => v.name === variableId);
    const id = variable?.id || variableId;
    const label = variable?.name || variableId;

    nodes.push({
      type: "mention",
      attrs: { id, label },
    });
    lastIndex = regex.lastIndex;
  }
  // Add any remaining text
  if (lastIndex < cleanedText.length) {
    nodes.push({
      type: "text",
      text: cleanedText.slice(lastIndex),
    });
  }
  const paragraph: JSONContent = { type: "paragraph" };

  if (nodes.length > 0) {
    paragraph.content = nodes;
  } else if (cleanedText.length > 0) {
    paragraph.content = [{ type: "text", text: cleanedText }];
  }

  return {
    type: "doc",
    content: [paragraph],
  };
}
